// src/pages/hr/DiscussQueue.tsx
//
// Queue of teaching-intention submissions flagged for an HR-leader follow-up
// because the teacher selected "I'd like to discuss my options" for FTE or day-off preference.
// Primary audience: Head of HR & People (AFranklin).
//
// Email notification path is tracked separately (see spawned follow-up task v1.2.1+).
// This widget is the pull/queue half; email is the push half.

import React, { useState, useEffect } from 'react';
import { MessageCircle, Calendar, User, AlertTriangle, RefreshCw, Inbox } from 'lucide-react';
import { supabase } from '../../supabase/config';
import { intentionPeriodsService } from '../../services/intentionPeriodsService';
import { IntentionPeriod } from '../../types/intentionPeriods';
import { displayLearningArea } from '../../constants/learningAreas';
import { getIntentionStatusMeta } from '../../utils/intentionStats';
import EmptyState from '../../components/ui/EmptyState';

interface DiscussQueueRow {
  id: string;
  staff_id: string;
  staff_name: string;
  department: string | null;
  period_id: string;
  status: string;
  submitted_at: string | null;
  updated_at: string | null;
  is_late: boolean;
  availability: any; // JSONB
}

function describeDiscussReason(availability: any): string {
  const parts: string[] = [];
  if (availability?.ftePreference === 'discuss') parts.push('FTE / load');
  if (availability?.dayOffPreference === 'discuss') parts.push('Day-off preference');
  return parts.length ? parts.join(' + ') : 'Unknown (check submission)';
}

function formatSubmittedAt(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

const DiscussQueue: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [rows, setRows] = useState<DiscussQueueRow[]>([]);
  const [periods, setPeriods] = useState<IntentionPeriod[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const all = await intentionPeriodsService.getAllPeriods();
        setPeriods(all);
        const open = all.find(p => p.status === 'open');
        if (open) setSelectedPeriodId(open.id);
      } catch {
        /* silent */
      }
    })();
  }, []);

  useEffect(() => {
    loadQueue();
  }, [selectedPeriodId]);

  const loadQueue = async () => {
    setLoading(prev => prev && true);
    setRefreshing(true);
    setError(null);
    try {
      let query = supabase
        .from('teaching_intentions')
        .select('id, staff_id, staff_name, department, period_id, status, submitted_at, updated_at, is_late, availability')
        .eq('hr_discuss_flag', true)
        .order('submitted_at', { ascending: true });

      if (selectedPeriodId !== 'all') {
        query = query.eq('period_id', selectedPeriodId);
      }

      const { data, error } = await query;
      if (error) throw error;
      setRows((data || []) as DiscussQueueRow[]);
    } catch (err: any) {
      setError(err?.message || 'Failed to load discuss queue');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-lg border border-ink-200 p-6 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ink-900 flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-navy-600" />
              Discuss Queue
            </h1>
            <p className="text-ink-500 mt-1">
              Teaching-intention submissions where the teacher selected <em>"I'd like to discuss my options"</em> for FTE or day-off preferences.
              Please reach out to each teacher individually to talk through their options.
            </p>
          </div>
          <button
            onClick={loadQueue}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-2 text-sm text-ink-700 bg-white border border-ink-300 rounded-md hover:bg-ink-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Period filter */}
        <div className="mt-4 flex items-center gap-3">
          <label htmlFor="period-select" className="text-sm font-medium text-ink-700">
            Period:
          </label>
          <select
            id="period-select"
            value={selectedPeriodId}
            onChange={e => setSelectedPeriodId(e.target.value)}
            className="px-3 py-1.5 border border-ink-300 rounded-md text-sm shadow-sm focus:ring-navy-500 focus:border-navy-500"
          >
            <option value="all">All periods</option>
            {periods.map(p => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.status})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-danger-bg border border-danger-border rounded-lg text-danger-fg flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-20 bg-ink-200 rounded" />
          <div className="h-20 bg-ink-200 rounded" />
          <div className="h-20 bg-ink-200 rounded" />
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-lg border border-ink-200">
          <EmptyState
            icon={<Inbox className="h-6 w-6" />}
            title="No submissions currently need a discussion."
            description={<>This queue populates when a teacher selects "I'd like to discuss my options" on their intention form.</>}
          />
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-ink-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-ink-50 border-b border-ink-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-500 uppercase tracking-wider">Teacher</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-500 uppercase tracking-wider">Learning Area</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-500 uppercase tracking-wider">Flagged for</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-500 uppercase tracking-wider">Submitted</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {rows.map(row => (
                <tr key={row.id} className="hover:bg-ink-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-ink-400 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-ink-900">{row.staff_name}</div>
                        <div className="text-xs text-ink-500 font-mono tabular-nums">{row.staff_id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-ink-700">{displayLearningArea(row.department, '—')}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-navy-100 text-navy-800 rounded text-xs font-medium border border-navy-200">
                      {describeDiscussReason(row.availability)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getIntentionStatusMeta(row.status).bg} ${getIntentionStatusMeta(row.status).text}`}>
                        {getIntentionStatusMeta(row.status).label}
                      </span>
                      {row.is_late && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-warn-bg text-warn-fg border border-warn-border">
                          Late
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-ink-500">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-ink-400 flex-shrink-0" />
                      <span className="font-mono tabular-nums">{formatSubmittedAt(row.submitted_at)}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DiscussQueue;
